import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql } from '@apollo/client';
import { UserInfoContext, type UserInfoContextType } from '@/contexts/UserInfoContext';
import { type UserFeatures, type UserInfo } from '@/types';
import { logger } from '@matrix-forge/shared/utils/logger';
import { publicClient, getAppPrefix } from '@/utils/apollo';

const FEATURES_POLL_INTERVAL_MS = 10_000;

// Query to get user details from server session (authenticated via cookie).
//
// `FeatureRouteGuard` evaluates the gate on the *first* render after a session
// restore — and a full-page reload from `MatrixAuth.TenantSwitchPlug.enter/1`
// counts as a fresh mount. Any feature the guard checks (see
// `FEATURE_GATED_ROUTES` in `routes/featureGatedRoutes.ts`) MUST appear here,
// otherwise it lands on `userInfo.features` as `undefined`, evaluates as falsy
// in `evaluateFeatureGate`, and the guard fires `navigate('/')` against a
// transient "denied" state. The follow-up `useUserDetailsWithFeatures` query
// fills the same shape but lands later — too late for the impersonation entry
// (the user is already at `/`). Keep this list in lockstep with the gated
// feature set; the SDK schema picks up the rest.
export const GET_USER_DETAILS_FOR_SESSION = gql`
  query GetUserDetailsForSession {
    userDetails {
      details
      features {
        agentCollaboration
        automationsToolsEnabled
        caseManagementToolsEnabled
        concreteModelSelection
        constructIntegration
        customAgents
        secretsManagementEnabled
        externalConnectors
        gitlabToolsEnabled
        knowledgebasesViewOnly
        knowledgebasesEdit
        mcpManagement
        mcpToolSelection
        personaBuilderEnabled
        secOpsToolsEnabled
        sentryUnmaskData
        workflowsManage
        webhooksEnabled
        workflowBuilderEnabled
        workflowAgentEnabled
        asocV2Enabled
        rbacEnabled
        tokenUsageTracking
        dataRoutingEnabled
        tokenPricing
        tokenUsageAdmin
        tokenUsageMonitor
        overviewEnabled
        customersViewDashboards
        editDashboards
        organizationManagement
        pinchartsEnabled
        rbvcEnabled
        strikekitEnabled
        systemAdministration
        documentSharingEnabled
        tenantWindowsEnabled
        hideConnectorPersonas
      }
      roles
      isAdmin
      isPlg
      isTokenAdmin
      canAccessAdminConsole
      defaultS3Endpoint
      defaultDevoEndpoint
      assumedRoleFeatures
    }
  }
`;

const FEATURE_KEYS_TO_WATCH: (keyof UserFeatures)[] = ['tokenUsageTracking'];

function featuresChanged(prev: UserFeatures | undefined, next: UserFeatures): boolean {
  return FEATURE_KEYS_TO_WATCH.some(key => prev?.[key] !== next[key]);
}

/**
 * A Context Provider to enable descendant nodes to access the UserInfoContext.
 *
 * Session restoration flow:
 * 1. On mount, query the server for user details using the session cookie
 * 2. If the session cookie is valid, server returns user info
 * 3. If no valid session, userInfo remains null -> triggers redirect to /auth/login
 *
 * No JWT is stored in localStorage - authentication is purely cookie-based.
 */
/**
 * Send an unauthenticated browser to the login flow instead of rendering nothing.
 *
 * Previously a failed session restore only wrote a debug line. `userInfo` stayed
 * null, the app rendered no routes, and the user was left on a BLANK PAGE — on
 * the short-session `timeout` tenant the body was literally the launcher orb and
 * nothing else. No login prompt, no error, no retry: a dead session was
 * indistinguishable from a hung one.
 *
 * `400` is treated as unauthenticated alongside `401` because that is what the
 * session endpoint actually returns once the gateway JWT is dead — keying only
 * on 401 meant the real expiry path never matched.
 *
 * Guarded against loops with a one-shot sessionStorage key: if login bounces us
 * straight back still-unauthenticated, we render normally rather than
 * ping-ponging. The key is cleared as soon as a session is established.
 */
const LOGIN_REDIRECT_GUARD = 'studio:auth-redirect-attempted';

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem(LOGIN_REDIRECT_GUARD)) {
      logger.debug('  Login redirect already attempted — not looping');
      return;
    }
    window.sessionStorage.setItem(LOGIN_REDIRECT_GUARD, '1');
  } catch {
    /* storage blocked — fall through and redirect once */
  }
  const prefix = getAppPrefix() || '/studio';
  const here = window.location.pathname + window.location.search + window.location.hash;
  window.location.assign(`${prefix}/auth/login?redirect=${encodeURIComponent(here)}`);
}

export function clearLoginRedirectGuard(): void {
  try {
    window.sessionStorage.removeItem(LOGIN_REDIRECT_GUARD);
  } catch {
    /* storage blocked */
  }
}

export const UserInfoProvider: FC<PropsWithChildren> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [userMetadata, setUserMetadata] = useState<string | null>(null);
  const [hasRestoredOnce, setHasRestoredOnce] = useState(false);
  const [featuresChangedPending, setFeaturesChangedPending] = useState(false);
  const userInfoRef = useRef<UserInfo | null>(null);

  // On mount: check for valid server session via cookie
  useEffect(() => {
    // Prevent restoration from running multiple times (React Strict Mode in dev)
    if (hasRestoredOnce) return;

    // `cancelled` short-circuits any `setState` after unmount. The async
    // query has no abort path through `publicClient.query`, so we let it
    // run to completion and just discard the result if the provider was
    // torn down (HMR, route teardown, parent Suspense boundary). Without
    // this, a stale resolved query can land `assumedRole` into a remounted
    // provider instance.
    let cancelled = false;

    const restoreSession = async () => {
      logger.debug('🔄 UserInfoProvider: Checking server session via cookie...');

      try {
        const { data, error } = await publicClient.query({
          query: GET_USER_DETAILS_FOR_SESSION,
          fetchPolicy: 'network-only',
        });

        if (cancelled) return;

        if (error) {
          logger.debug('  Server session query failed:', error.message);
          setHasRestoredOnce(true);
          return;
        }

        if (data?.userDetails?.details) {
          logger.debug('  Got server session response');
          const details =
            typeof data.userDetails.details === 'string'
              ? JSON.parse(data.userDetails.details)
              : data.userDetails.details;

          // Extract user info from server response.
          // SENSITIVE: `standAloneToken` and `jwt` are credentials. They
          // live in component state so callers (Apollo links, downstream
          // hooks) can read them; never include `userInfo` verbatim in
          // Sentry breadcrumbs or error payloads — the Sentry beforeSend
          // pipeline does not auto-scrub these field names.
          const tenantSwitch = details.tenantSwitch ?? {};
          const restoredUserInfo: UserInfo = {
            id: details.id || details.userDomain?.id,
            name: details.userName || details.userDomain?.userName || '',
            email: details.email || details.userDomain?.email || '',
            standAloneToken: details.credential?.standAloneToken || '',
            serrea: '',
            adaURI: '',
            alertsURI: '',
            activeboardsURI: '',
            devoDomain: details.domain?.name || details.domainInfo?.name || '',
            devoDomainId: details.domain?.id || details.domainInfo?.id || '',
            jwt: details.jwt || details.credential?.jwtSessionCredentials?.jwtUserId || '',
            features: data.userDetails.features,
            roles: data.userDetails.roles || [],
            isAdmin: data.userDetails.isAdmin ?? false,
            isPlg: data.userDetails.isPlg ?? false,
            isTokenAdmin: data.userDetails.isTokenAdmin ?? false,
            canAccessAdminConsole: data.userDetails.canAccessAdminConsole ?? false,
            defaultS3Endpoint: data.userDetails.defaultS3Endpoint ?? null,
            defaultDevoEndpoint: data.userDetails.defaultDevoEndpoint ?? null,
            tenantSwitched: Boolean(tenantSwitch.switched),
            originalTenantName: tenantSwitch.originalTenantName ?? null,
            assumedRole: tenantSwitch.assumedRole ?? null,
            assumedRoleFeatures: data.userDetails.assumedRoleFeatures ?? null,
          };

          logger.debug('✅ Session restored from server!', {
            id: restoredUserInfo.id,
            email: restoredUserInfo.email,
          });

          if (cancelled) return;
          setUserInfo(restoredUserInfo);
          setUserMetadata(JSON.stringify(details));
          clearLoginRedirectGuard();
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const networkError = (err as { networkError?: { statusCode?: number } })?.networkError;
        const message = err instanceof Error ? err.message : String(err);
        const status = networkError?.statusCode;
        const unauthenticated =
          status === 401 || status === 400 || message.includes('Not authenticated');

        if (unauthenticated) {
          logger.debug('  No valid server session (not authenticated)');
          redirectToLogin();
        } else {
          logger.debug('  Server session check failed:', message);
        }
      }

      if (!cancelled) setHasRestoredOnce(true);
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, [hasRestoredOnce]);

  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const poll = useCallback(async () => {
    if (!userInfoRef.current) return;
    try {
      const { data } = await publicClient.query({
        query: GET_USER_DETAILS_FOR_SESSION,
        fetchPolicy: 'network-only',
      });

      const nextFeatures: UserFeatures | undefined = data?.userDetails?.features;
      if (nextFeatures && featuresChanged(userInfoRef.current?.features, nextFeatures)) {
        logger.debug('[UserInfoProvider] Feature flags changed — applying to UI');
        setUserInfo(prev => (prev ? { ...prev, features: nextFeatures } : prev));
        setFeaturesChangedPending(true);
      }
    } catch {
      // Silent — polling errors should not disrupt the user experience.
    }
  }, []);

  useEffect(() => {
    if (!hasRestoredOnce) return;

    let timerId: ReturnType<typeof setInterval> | null = null;

    const startInterval = () => {
      if (timerId !== null) return;
      timerId = setInterval(poll, FEATURES_POLL_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (timerId === null) return;
      clearInterval(timerId);
      timerId = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        poll();
        startInterval();
      } else {
        stopInterval();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    if (document.visibilityState === 'visible') {
      startInterval();
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopInterval();
    };
  }, [hasRestoredOnce, poll]);

  const dismissFeaturesChanged = useCallback(() => setFeaturesChangedPending(false), []);

  const value = useMemo<UserInfoContextType>(
    () => ({
      userInfo,
      setUserInfo,
      userMetadata,
      setUserMetadata,
      isLoading: !hasRestoredOnce,
      featuresChangedPending,
      dismissFeaturesChanged,
      checkForFeatureChanges: poll,
    }),
    [userInfo, userMetadata, hasRestoredOnce, featuresChangedPending, dismissFeaturesChanged, poll]
  );
  return <UserInfoContext value={value}>{children}</UserInfoContext>;
};

UserInfoProvider.displayName = 'UserInfoProvider';
